import { useLazyQuery, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Body1,
    Button,
    Heading1,
    Heading2,
    Stack,
} from '@myphx-engineering/phx-design-system';

import {
    GetAnonymousPrescriberOrderDocument,
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
    SendPrescriberOrderAnonymousDocument,
} from '../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../util';
import { LocationInfo } from '../common/LocationInfo';

type SimplifiedPreviewDetailsProps = {
    location: PharmacyProviderInfoFragment | MailOrderProviderInfoFragment;
    drxId: string;
};

export const SimplifiedPreviewDetails = ({
    location,
    drxId,
}: SimplifiedPreviewDetailsProps) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const [sendAnonymousPrescriberOrder, { data, error, called }] = useMutation(
        SendPrescriberOrderAnonymousDocument,
        {
            onCompleted: () => {
                setTimeout(() => {
                    getUpdatedPrescriberOrder({
                        variables: { drxId },
                    });
                }, 10000);
            },
        }
    );

    const [getUpdatedPrescriberOrder] = useLazyQuery(
        GetAnonymousPrescriberOrderDocument,
        {
            fetchPolicy: 'network-only',
            onCompleted: () => {
                setIsLoading(false);
            },
        }
    );

    const handleSend = () => {
        setIsLoading(true);
        sendAnonymousPrescriberOrder({
            variables: {
                prescriberOrderId: drxId,
                providerId: location.id,
            },
        });
    };

    if (
        called &&
        !isLoading &&
        error &&
        !data?.sendPrescriberOrderAnonymous?.success
    ) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    return (
        <Stack gap="md" mih="80vh">
            <Stack gap="md" flex={1}>
                <Heading1>{t('previewDetails.title')}</Heading1>
                <Stack gap="sm">
                    <Heading2>{t('previewDetails.heading')}</Heading2>
                    <Body1>{t('previewDetails.description.pickUp')}</Body1>
                    <LocationInfo location={location} />
                </Stack>
            </Stack>

            <Button size="lg" loading={isLoading} onClick={handleSend}>
                {t('previewDetails.sendToPharmacy')}
            </Button>
        </Stack>
    );
};
